import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { stringSorter } from "../../utils/functions.js";
import DataTable from "../../components/CustomDatatable/index.js";
import {
  Button,
  Card,
  Space,
  Typography,
  Breadcrumb,
  Row,
  Col,
  Popconfirm,
  Switch,
} from "antd";
import { EditOutlined, HomeOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Spinner } from "reactstrap";
import SeriesForm from "./ModelsSeriesForm.js";
import { enhanceColumnsWithFilters } from "../../components/ColumnFilters/index.js";
import { addSeries, deleteSeries, getSeries, updateSeries } from "../../store/actions/series.js";
import { getBrands } from "../../store/actions/brand.js";
import moment from "moment-timezone";

const { Title } = Typography;

const Series = () => {
  document.title = "Series";
  const [isLoading, setIsLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const dispatch = useDispatch();
  const seriesList = useSelector((state) => state.series.list);
  const brandList = useSelector((state) => state.brand.list);

  // Build a map for quick brand name lookup
  const brandMap = brandList.reduce((acc, brand) => {
    acc[brand.id] = brand.name;
    return acc;
  }, {});

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(getBrands());
        await dispatch(getSeries());
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  // ─── Create / Update ────────────────────────────────────────────────────────
  const handleCreate = async (values) => {
    if (selectedRecord) {
      // Only send changed fields
      const changedFields = {};

      if (values.name !== selectedRecord.name) {
        changedFields.name = values.name;
      }
      if (values.brandId !== selectedRecord.brandid) {
        changedFields.brandid = Number(values.brandId);
      }
      if (values.ordinal !== selectedRecord.ordinal) {
        changedFields.ordinal = values.ordinal;
      }

      changedFields.id = selectedRecord.id; // always send id

      await dispatch(updateSeries(changedFields));
    } else {
      // CREATE — send all fields
      const payload = {
        name: values.name,
        brandid: Number(values.brandId),
        isForAll: values.isForAll ?? false,
        ...(values.ordinal !== undefined && values.ordinal !== null && { ordinal: values.ordinal }),
      };

      await dispatch(addSeries(payload));
    }

    await dispatch(getSeries());
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  // ─── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    await dispatch(deleteSeries(id));
    await dispatch(getSeries());
  };

  // ─── Toggle status ───────────────────────────────────────────────────────────
  const handleChangeStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    await dispatch(updateSeries({ id, status: newStatus }));
    await dispatch(getSeries());
  };

  // ─── Open edit modal ─────────────────────────────────────────────────────────
  const handleEdit = (record) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
  };

  // ─── Columns ─────────────────────────────────────────────────────────────────
  let columns = [
    {
      title: "Series Name",
      dataIndex: "name",
      key: "name",
      sorter: stringSorter("name"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Brand",
      dataIndex: "brandName",
      key: "brandName",
      sorter: stringSorter("brandName"),
      sortDirections: ["ascend", "descend"],
      render: (brandName) => brandName || "-",
    },
    {
      title: "Ordinal",
      dataIndex: "ordinal",
      key: "ordinal",
      sorter: (a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0),
      sortDirections: ["ascend", "descend"],
      render: (val) => (val !== undefined && val !== null ? val : "—"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => (
        <Switch
          checked={text === 1}
          onChange={() => handleChangeStatus(record.id, text)}
        />
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) =>
        moment(date).isValid() ? moment(date).format("YYYY-MM-DD HH:mm:ss") : "",
      sorter: (a, b) =>
        moment(a.createdAt).isValid() && moment(b.createdAt).isValid()
          ? moment(a.createdAt).unix() - moment(b.createdAt).unix()
          : 0,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          <Popconfirm
            title="Are you sure you want to delete this record?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} title="Delete" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  columns = enhanceColumnsWithFilters(columns);

  return (
    <div className="page-content">
      <Breadcrumb style={{ marginBottom: "16px" }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>Series</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                Manage Series
              </Title>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedRecord(null);
                  setIsModalVisible(true);
                }}
              >
                Add Series
              </Button>
            </Col>
          </Row>

          {isLoading ? (
            <div className="d-flex justify-content-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <DataTable
              dataSource={seriesList.map((item) => ({ ...item, key: item.id }))}
              columns={columns}
            />
          )}
        </Space>
      </Card>

      <SeriesForm
        visible={isModalVisible}
        onCreate={handleCreate}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
      />
    </div>
  );
};

export default Series;